import { useEffect, useRef, useState } from "react";
import { parseTwitchPlayerEmbedChannel } from "../../../services/twitchServices";

const TOOLBAR_HEIGHT = 34;
const ASPECT_RATIO = 9 / 16;
const DEFAULT_WIDTH = 560;
const VIEWPORT_MARGIN = 16;
const PLAYER_MIN_WIDTH = 200;
const CHAT_MIN_WIDTH = 160;
const CHAT_WIDTH_RATIO = 0.34;

export type PopoutLayout = {
  x: number;
  y: number;
  width: number;
};

export type ResizeCorner = "ne" | "nw" | "se" | "sw";

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  startLayout: PopoutLayout;
};

type ResizeState = {
  pointerId: number;
  corner: ResizeCorner;
  startX: number;
  startY: number;
  startLayout: PopoutLayout;
};

const getChatPanelWidth = (totalWidth: number) =>
  Math.min(
    Math.max(CHAT_MIN_WIDTH, Math.round(totalWidth * CHAT_WIDTH_RATIO)),
    totalWidth - PLAYER_MIN_WIDTH,
  );

const getMinWidth = (chatOpen: boolean) => (chatOpen ? PLAYER_MIN_WIDTH + CHAT_MIN_WIDTH : 240);

const getPopoutMetrics = (totalWidth: number, chatOpen: boolean) => {
  const chatWidth = chatOpen ? getChatPanelWidth(totalWidth) : 0;
  const playerWidth = totalWidth - chatWidth;
  const playerHeight = playerWidth * ASPECT_RATIO;
  const totalHeight = TOOLBAR_HEIGHT + playerHeight;

  return { chatWidth, playerWidth, playerHeight, totalHeight };
};

const getDefaultLayout = (chatOpen: boolean): PopoutLayout => {
  const width = Math.min(DEFAULT_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  const { totalHeight } = getPopoutMetrics(width, chatOpen);
  return {
    x: window.innerWidth - VIEWPORT_MARGIN - width,
    y: window.innerHeight - VIEWPORT_MARGIN - totalHeight,
    width,
  };
};

const clampWidth = (width: number, chatOpen: boolean) => {
  const maxWidth = window.innerWidth - VIEWPORT_MARGIN * 2;
  return Math.min(Math.max(getMinWidth(chatOpen), width), maxWidth);
};

const clampLayout = (layout: PopoutLayout, chatOpen: boolean): PopoutLayout => {
  const width = clampWidth(layout.width, chatOpen);
  const { totalHeight } = getPopoutMetrics(width, chatOpen);

  return {
    width,
    x: Math.min(
      Math.max(VIEWPORT_MARGIN, layout.x),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
    ),
    y: Math.min(
      Math.max(VIEWPORT_MARGIN, layout.y),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - totalHeight - VIEWPORT_MARGIN),
    ),
  };
};

const applyResize = (
  corner: ResizeCorner,
  start: PopoutLayout,
  deltaX: number,
  chatOpen: boolean,
): PopoutLayout => {
  const { totalHeight: startHeight } = getPopoutMetrics(start.width, chatOpen);
  const left = start.x;
  const top = start.y;
  const right = start.x + start.width;
  const bottom = start.y + startHeight;

  let width = start.width;
  let x = left;
  let y = top;

  switch (corner) {
    case "se":
      width = clampWidth(start.width + deltaX, chatOpen);
      break;
    case "sw":
      width = clampWidth(start.width - deltaX, chatOpen);
      x = right - width;
      break;
    case "ne":
      width = clampWidth(start.width + deltaX, chatOpen);
      y = bottom - getPopoutMetrics(width, chatOpen).totalHeight;
      break;
    case "nw":
      width = clampWidth(start.width - deltaX, chatOpen);
      x = right - width;
      y = bottom - getPopoutMetrics(width, chatOpen).totalHeight;
      break;
  }

  return clampLayout({ x, y, width }, chatOpen);
};

interface UsePopoutDragResizeArgs {
  embedSrc: string;
  chatOpen: boolean;
  offsetIndex?: number;
}

export const usePopoutDragResize = ({
  embedSrc,
  chatOpen,
  offsetIndex = 0,
}: UsePopoutDragResizeArgs) => {
  const popoutRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<DragState | null>(null);
  const resizeState = useRef<ResizeState | null>(null);
  const [playerSrc, setPlayerSrc] = useState(embedSrc);
  const [layout, setLayout] = useState<PopoutLayout>(() => {
    const baseLayout = getDefaultLayout(chatOpen);
    return {
      ...baseLayout,
      x: baseLayout.x - offsetIndex * 56,
      y: Math.max(VIEWPORT_MARGIN, baseLayout.y - offsetIndex * 44),
    };
  });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    const currentChannel = parseTwitchPlayerEmbedChannel(playerSrc);
    const nextChannel = parseTwitchPlayerEmbedChannel(embedSrc);
    if (nextChannel !== "" && currentChannel !== nextChannel) {
      setPlayerSrc(embedSrc);
    }
  }, [embedSrc, playerSrc]);

  useEffect(() => {
    setLayout((current) => clampLayout(current, chatOpen));
  }, [chatOpen]);

  useEffect(() => {
    const onWindowResize = () => {
      setLayout((current) => clampLayout(current, chatOpen));
    };

    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, [chatOpen]);

  useEffect(() => {
    if (!dragging && !resizing) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      if (dragState.current && event.pointerId === dragState.current.pointerId) {
        const { startX, startY, startLayout } = dragState.current;
        setLayout(
          clampLayout(
            {
              ...startLayout,
              x: startLayout.x + (event.clientX - startX),
              y: startLayout.y + (event.clientY - startY),
            },
            chatOpen,
          ),
        );
        return;
      }

      if (resizeState.current && event.pointerId === resizeState.current.pointerId) {
        const { corner, startX, startLayout } = resizeState.current;
        setLayout(applyResize(corner, startLayout, event.clientX - startX, chatOpen));
      }
    };

    const endInteraction = (event: PointerEvent) => {
      if (dragState.current?.pointerId === event.pointerId) {
        dragState.current = null;
        setDragging(false);
      }
      if (resizeState.current?.pointerId === event.pointerId) {
        resizeState.current = null;
        setResizing(false);
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endInteraction);
    window.addEventListener("pointercancel", endInteraction);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endInteraction);
      window.removeEventListener("pointercancel", endInteraction);
    };
  }, [chatOpen, dragging, resizing]);

  const handleDragPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLayout: layout,
    };
    setDragging(true);
    event.preventDefault();
  };

  const handleResizePointerDown =
    (corner: ResizeCorner) => (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      resizeState.current = {
        pointerId: event.pointerId,
        corner,
        startX: event.clientX,
        startY: event.clientY,
        startLayout: layout,
      };
      setResizing(true);
    };

  const stopToolbarPointer = (event: React.PointerEvent) => {
    event.stopPropagation();
  };

  const { playerWidth, playerHeight, chatWidth } = getPopoutMetrics(layout.width, chatOpen);

  return {
    popoutRef,
    playerSrc,
    layout,
    dragging,
    resizing,
    playerWidth,
    playerHeight,
    chatWidth,
    handleDragPointerDown,
    handleResizePointerDown,
    stopToolbarPointer,
  };
};
